import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  Divider,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { endpoints } from "@/configs/Settings";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";
import { useAxios } from "@/utils/useAxios";
import { useAuth } from "@/utils/useAuth";
import { HTTPResponseStatus } from "@/utils/enum";
import { NoData } from "@/components/common/NoData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`conduct-tabpanel-${index}`}
      aria-labelledby={`conduct-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ConductData {
  conduct: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
  patientName?: string;
}

interface PatientConductGroup {
  patientId: string;
  patientName: string;
  totalConducts: number;
  conducts: ConductData[];
  statusCounts: {
    realizada: number;
    aberta: number;
    parcial: number;
    "não realizada": number;
  };
}

interface ConductStats {
  status: string;
  count: number;
  percentage: number;
}

export function ConductAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [conducts, setConducts] = useState<ConductData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientConducts, setPatientConducts] = useState<ConductData[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(
    new Map()
  );
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [conductSearch, setConductSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de condutas atualizados:", filters);
    },
  });

  const author = `${oids.cnpj}-${organizationId}`;
  const practitionerFilter = selectedPractitioner || "all";

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Função para obter o nome do profissional a partir da referência
  const getPractitionerName = useCallback(
    (practitionerReference: string) => {
      if (!practitionerReference) return "Não informado";

      // Extrai o ID do profissional da referência
      const practId = practitionerReference.split("/")[1];

      // Busca o recurso Practitioner correspondente
      const pract = practitioner?.find((p: any) => p.resource?.id === practId);

      // Retorna o nome formatado
      const displayName =
        pract?.resource?.name?.[0]?.text ||
        `${pract?.resource?.name?.[0]?.given?.join(" ") || ""} ${
          pract?.resource?.name?.[0]?.family || ""
        }`.trim();

      return displayName || practId;
    },
    [practitioner]
  );

  // Função para buscar dados dos pacientes
  const fetchPatients = async (patientIds: string[]) => {
    try {
      const patientMap = new Map<string, fhir5.Patient>();

      for (const patientRef of patientIds) {
        const patientId = patientRef.split("/")[1];
        if (!patientId || patientMap.has(patientId)) continue;

        const url = `${endpoints.MPIFHIR}/Patient/${patientId}`;
        const headers = {
          Authorization: await useAuth.getUserToken(),
          "Content-Type": "application/fhir+json",
          Accept: "application/fhir+json",
        };

        try {
          const response = await useAxios.get(url, { headers, timeout: 20000 });
          if (response.status === HTTPResponseStatus.Success && response.data) {
            patientMap.set(patientId, response.data);
          }
        } catch (err) {
          console.error(`Erro ao buscar paciente ${patientId}:`, err);
        }
      }

      setPatients(patientMap);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
    }
  };

  // Buscar dados das condutas
  const fetchConductsData = useCallback(async () => {
    if (!author) return;

    setLoading(true);
    try {
      // Buscar QuestionnaireResponse sem hasProtocols para pegar condutas
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });

      const entries: any[] = response?.entry || [];

      // Map para agrupar condutas por paciente + nome da conduta
      const conductMap = new Map<
        string,
        {
          conduct: string;
          status: string;
          practitioner: string;
          date: string;
          patientId: string;
          authored: Date;
        }
      >();

      for (const entry of entries) {
        const resource = entry?.resource;
        if (!resource || resource.resourceType !== "QuestionnaireResponse")
          continue;

        // Buscar o item de Condutas (linkId: "38adde2c-f083-435f-8a1f-fc9cc6fa740a")
        const conductsItem = resource.item?.find(
          (item: any) => item.linkId === "38adde2c-f083-435f-8a1f-fc9cc6fa740a"
        );

        if (!conductsItem?.item) continue;

        // ID do paciente
        const patientId = resource.subject?.reference || "unknown";

        // Nome do profissional - usa a função que busca do Practitioner
        const practitionerName = getPractitionerName(
          resource.author?.reference || ""
        );

        // Data do atendimento
        const authoredDate = resource.authored
          ? new Date(resource.authored)
          : new Date(0);
        const date = authoredDate.toLocaleDateString();

        // Processar cada conduta (linkId: "a6f78af6-6fd9-49a7-e606-491065403d2d")
        const seenInThisQuestionnaire = new Set<string>();

        for (const conductGroup of conductsItem.item) {
          if (
            !conductGroup.item ||
            conductGroup.linkId !== "a6f78af6-6fd9-49a7-e606-491065403d2d"
          )
            continue;

          // Buscar a conduta (linkId: "3cccab72-a2ed-46e9-ebce-a1c6532e4bca")
          const conductItem = conductGroup.item.find(
            (item: any) =>
              item.linkId === "3cccab72-a2ed-46e9-ebce-a1c6532e4bca"
          );

          const conductText = conductItem?.answer?.[0]?.valueString;
          if (!conductText) continue; // Pula se não tiver texto de conduta

          // Buscar o status da conduta (linkId: "2e202c22-d5bc-4243-870e-d342cb9c1b8d")
          const statusItem = conductGroup.item.find(
            (item: any) =>
              item.linkId === "2e202c22-d5bc-4243-870e-d342cb9c1b8d"
          );

          // Se não tiver status, está em aberto
          let status = "aberta";

          if (statusItem?.answer?.[0]?.valueCoding) {
            const statusCode = statusItem.answer[0].valueCoding.code;
            const statusDisplay = statusItem.answer[0].valueCoding.display;

            if (statusCode === "concluded" || statusDisplay === "Realizada") {
              status = "realizada";
            } else if (
              statusCode === "partially" ||
              statusDisplay === "Parcialmente"
            ) {
              status = "parcial";
            } else if (
              statusCode === "failed" ||
              statusDisplay === "Não realizada"
            ) {
              status = "não realizada";
            }
          }

          // Chave única para evitar duplicatas no mesmo questionário
          // - Com resposta (status != "aberta"): conduta + status
          // - Sem resposta (status == "aberta"): apenas conduta
          const uniqueKey =
            status === "aberta" ? conductText : `${conductText}-${status}`;

          // Evita duplicatas no mesmo questionário usando a mesma lógica de chave
          if (seenInThisQuestionnaire.has(uniqueKey)) continue;
          seenInThisQuestionnaire.add(uniqueKey);

          // Chave única global:
          // - Com resposta (status != "aberta"): paciente + conduta + status
          // - Sem resposta (status == "aberta"): paciente + conduta
          const key =
            status === "aberta"
              ? `${patientId}-${conductText}`
              : `${patientId}-${conductText}-${status}`;

          // Se já existe, mantém o mais recente
          const existing = conductMap.get(key);
          if (!existing || authoredDate > existing.authored) {
            conductMap.set(key, {
              conduct: conductText,
              status,
              practitioner: practitionerName,
              date,
              patientId,
              authored: authoredDate,
            });
          }
        }
      }

      // Aplicar lógica de paridade (igual ao formulário original)
      // Regras:
      // - Agrupar por paciente + texto da conduta (não por status)
      // - PAR: mostra apenas 1 registro (com status ou mais recente)
      // - ÍMPAR: mostra até 2 registros (um com status + um sem status)
      // - EXCEÇÃO: Sempre manter itens do QuestionnaireResponse mais recente do paciente
      const patientLatestAuthored = new Map<string, number>();
      for (const v of conductMap.values()) {
        const t = v.authored.getTime();
        const prev = patientLatestAuthored.get(v.patientId) || 0;
        if (t > prev) patientLatestAuthored.set(v.patientId, t);
      }

      // Agrupar por paciente + conduta (não por status)
      const groups = new Map<
        string,
        Array<{
          conduct: string;
          status: string;
          practitioner: string;
          date: string;
          patientId: string;
          authored: Date;
        }>
      >();

      for (const value of conductMap.values()) {
        const key = `${value.patientId}|${value.conduct}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(value);
      }

      const parityConducts: ConductData[] = [];
      for (const [, items] of groups.entries()) {
        if (items.length === 1) {
          parityConducts.push({
            conduct: items[0].conduct,
            status: items[0].status,
            practitioner: items[0].practitioner,
            date: items[0].date,
            patientId: items[0].patientId,
          });
          continue;
        }

        // Ordena por data (mais recente primeiro)
        const sorted = items.sort(
          (a, b) => b.authored.getTime() - a.authored.getTime()
        );
        const isPair = items.length % 2 === 0;
        const withStatus = sorted.filter(
          (it) => it.status && it.status !== "aberta"
        );
        const withoutStatus = sorted.filter(
          (it) => !it.status || it.status === "aberta"
        );

        const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
        const latestBelongsToCurrentQR =
          latestAuthoredTs !== undefined &&
          sorted[0].authored.getTime() === latestAuthoredTs;

        // Se pertence ao QR mais recente, sempre manter TODAS as entradas
        if (latestBelongsToCurrentQR) {
          // Para QR mais recente, sempre manter todas as entradas (ignorar paridade)
          for (const item of sorted) {
            parityConducts.push({
              conduct: item.conduct,
              status: item.status,
              practitioner: item.practitioner,
              date: item.date,
              patientId: item.patientId,
            });
          }
        } else {
          // Lógica normal de paridade
          if (isPair) {
            // PAR: apenas 1 registro
            if (withStatus.length > 0) {
              parityConducts.push({
                conduct: withStatus[0].conduct,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
            } else {
              parityConducts.push({
                conduct: sorted[0].conduct,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          } else {
            // ÍMPAR: até 2 registros
            if (withStatus.length > 0) {
              parityConducts.push({
                conduct: withStatus[0].conduct,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
              if (withoutStatus.length > 0) {
                parityConducts.push({
                  conduct: withoutStatus[0].conduct,
                  status: withoutStatus[0].status,
                  practitioner: withoutStatus[0].practitioner,
                  date: withoutStatus[0].date,
                  patientId: withoutStatus[0].patientId,
                });
              }
            } else {
              parityConducts.push({
                conduct: sorted[0].conduct,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          }
        }
      }

      setConducts(parityConducts);

      // Buscar dados dos pacientes
      const uniquePatientIds = Array.from(
        new Set(parityConducts.map((c) => c.patientId))
      );
      await fetchPatients(uniquePatientIds);
    } catch (error) {
      console.error("Erro ao buscar condutas:", error);
      setConducts([]);
    } finally {
      setLoading(false);
    }
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    getPractitionerName,
  ]);

  useEffect(() => {
    if (author) {
      fetchConductsData();
    }
  }, [fetchConductsData, author]);

  // Agrupar condutas por paciente
  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientConductGroup>();

    for (const conduct of conducts) {
      const patientId = conduct.patientId.split("/")[1];
      const patient = patients.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalConducts: 0,
          conducts: [],
          statusCounts: {
            realizada: 0,
            aberta: 0,
            parcial: 0,
            "não realizada": 0,
          },
        });
      }

      const group = groups.get(patientId)!;
      group.totalConducts++;
      group.conducts.push({ ...conduct, patientName });

      // Contar status
      const status = conduct.status || "aberta";
      if (status in group.statusCounts) {
        group.statusCounts[status as keyof typeof group.statusCounts]++;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.totalConducts - a.totalConducts
    );
  }, [conducts, patients]);

  // Processar estatísticas de condutas
  const { conductStats, totalConducts } = useMemo(() => {
    const statusMap = new Map<string, number>();

    for (const conduct of conducts) {
      const status = conduct.status || "aberta";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    }

    const total = conducts.length;
    const stats: ConductStats[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      conductStats: stats,
      totalConducts: total,
    };
  }, [conducts]);

  // Processar todas as condutas agrupadas
  const allConductsData = useMemo(() => {
    const conductMap = new Map<string, number>();
    for (const conduct of conducts) {
      conductMap.set(
        conduct.conduct,
        (conductMap.get(conduct.conduct) || 0) + 1
      );
    }
    return Array.from(conductMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [conducts]);

  // Processar top 5 condutas para o gráfico
  const topConductsData = useMemo(() => {
    return allConductsData.slice(0, 5);
  }, [allConductsData]);

  // Filtrar condutas para a tabela de pesquisa
  const filteredConductsData = useMemo(() => {
    if (!conductSearch.trim()) return allConductsData;
    const q = conductSearch.toLowerCase();
    return allConductsData.filter((item) =>
      item.label.toLowerCase().includes(q)
    );
  }, [allConductsData, conductSearch]);

  // Mapa de tradução de status
  const statusTranslation: Record<string, string> = {
    realizada: "Realizada",
    aberta: "Em Aberto",
    parcial: "Parcialmente",
    "não realizada": "Não Realizada",
  };

  // Dados para o gráfico de pizza (agora barra)
  const chartData = useMemo(() => {
    return conductStats.map((stat, idx) => ({
      id: idx,
      value: stat.count,
      percentage: stat.percentage,
      label:
        statusTranslation[stat.status] ||
        stat.status.charAt(0).toUpperCase() + stat.status.slice(1),
    }));
  }, [conductStats]);

  // Função para lidar com o clique na linha da tabela
  const handleRowClick = (row: any) => {
    const group = patientGroups.find((g) => g.patientName === row.patientName);
    if (group) {
      setSelectedPatient(group);
      setPatientConducts(group.conducts);
      setDialogSearch(""); // Resetar busca ao abrir o diálogo
      setPatientDialogOpen(true);
    }
  };

  // Filtrar grupos de pacientes baseado na busca
  const filteredPatientGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? patientGroups.filter(
          (group) =>
            group.patientName?.toLowerCase().includes(q) ||
            group.conducts.some(
              (c) =>
                c.conduct?.toLowerCase().includes(q) ||
                c.status?.toLowerCase().includes(q)
            )
        )
      : patientGroups;

    return filtered.map((group) => ({
      patientName: group.patientName,
      totalConducts: group.totalConducts,
      realizada: group.statusCounts.realizada,
      aberta: group.statusCounts.aberta,
      parcial: group.statusCounts.parcial,
      naoRealizada: group.statusCounts["não realizada"],
    }));
  }, [patientGroups, search]);

  // Filtrar condutas do diálogo baseado na busca
  const filteredDialogConducts = useMemo(() => {
    const q = dialogSearch.trim().toLowerCase();
    return q
      ? patientConducts.filter(
          (conduct) =>
            conduct.conduct?.toLowerCase().includes(q) ||
            conduct.status?.toLowerCase().includes(q) ||
            conduct.practitioner?.toLowerCase().includes(q)
        )
      : patientConducts;
  }, [patientConducts, dialogSearch]);

  // Renderizar skeleton durante carregamento
  if (loading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Condutas"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {totalConducts > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Dados Gerais" />
              <Tab label="Detalhamento por Paciente" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                <Stack spacing={5}>
                  {/* Gráfico de Status das Condutas */}
                  {chartData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const sorted = [...chartData].sort(
                            (a, b) => b.value - a.value
                          );
                          const total = chartData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((sorted[0].value / total) * 100)
                              : 0;
                          return `${percentage}% das condutas estão como '${sorted[0].label}'`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Verifique se as condutas recomendadas estão sendo
                        seguidas. Um número alto de condutas "Em Aberto" pode
                        indicar que o paciente não retornou ou que o registro
                        não foi atualizado.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(chartData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = chartData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {chartData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...chartData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de status de condutas." />
                  )}

                  {/* Gráfico de Top 5 Condutas */}
                  {topConductsData.length > 0 && <Divider />}

                  {topConductsData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const total = topConductsData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round(
                                  (topConductsData[0].value / total) * 100
                                )
                              : 0;
                          return `'${topConductsData[0].label}' representa ${percentage}% das condutas`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Veja as 5 orientações mais registradas pelos
                        profissionais. Como são textos livres, use a tabela
                        abaixo para pesquisar todas as condutas cadastradas.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={topConductsData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(topConductsData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = allConductsData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {topConductsData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...topConductsData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* Tabela de todas as condutas */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Todas as Condutas ({allConductsData.length} tipos
                          diferentes)
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="Buscar conduta..."
                            value={conductSearch}
                            onChange={(e) => setConductSearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <GenericTable
                          tableColumns={[
                            {
                              column: "Conduta",
                              rowName: "label",
                              align: "left",
                            },
                            {
                              column: "Quantidade",
                              rowName: "value",
                              width: "120px",
                            },
                            {
                              column: "Percentual",
                              rowName: "percentage",
                              width: "120px",
                            },
                          ]}
                          tableRows={filteredConductsData.map((item) => {
                            const total = allConductsData.reduce(
                              (acc, i) => acc + i.value,
                              0
                            );
                            const percentage =
                              total > 0
                                ? Math.round((item.value / total) * 100)
                                : 0;
                            return {
                              ...item,
                              percentage: `${percentage}%`,
                            };
                          })}
                          characterLimit={80}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de condutas registradas." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por paciente ou conduta"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    {
                      column: "Paciente",
                      rowName: "patientName",
                      align: "left",
                    },
                    {
                      column: "Realizada",
                      rowName: "realizada",
                      width: "100px",
                    },
                    { column: "Aberta", rowName: "aberta", width: "80px" },
                    { column: "Parcial", rowName: "parcial", width: "80px" },
                    {
                      column: "Não Realizada",
                      rowName: "naoRealizada",
                      width: "120px",
                    },
                    {
                      column: "Total",
                      rowName: "totalConducts",
                      width: "80px",
                    },
                  ]}
                  tableRows={filteredPatientGroups}
                  characterLimit={60}
                  onRowClick={handleRowClick}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhuma conduta encontrada
          </Typography>
        </Box>
      )}

      {/* Dialog para exibir condutas do paciente */}
      {patientDialogOpen && selectedPatient && (
        <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Condutas de: {selectedPatient.patientName}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Buscar por conduta, status ou profissional"
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredDialogConducts.length > 0 ? (
              <GenericTable
                tableColumns={[
                  { column: "Conduta", rowName: "conduct", align: "left" },
                  { column: "Status", rowName: "statusLabel", width: "150px" },
                  { column: "Profissional", rowName: "practitioner" },
                  { column: "Data", rowName: "date", width: "120px" },
                ]}
                tableRows={filteredDialogConducts.map((conduct) => ({
                  conduct: conduct.conduct,
                  statusLabel: conduct.status
                    ? conduct.status.charAt(0).toUpperCase() +
                      conduct.status.slice(1)
                    : "Não informado",
                  practitioner: conduct.practitioner,
                  date: conduct.date,
                }))}
                characterLimit={80}
              />
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 2 }}>
                {dialogSearch
                  ? "Nenhuma conduta encontrada com os critérios de busca."
                  : "Nenhuma conduta encontrada para este paciente."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPatientDialogOpen(false);
                setSelectedPatient(null);
                setPatientConducts([]);
                setDialogSearch("");
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
